import {useLocation, useNavigation} from '@github-ui/react-router'
import type {Location} from '@github-ui/react-router'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {useRouteQuery, useSuspenseRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {useRepositoryFilterActions} from '@github-ui/issues-shared/repository-filter-actions'
import {AnalyticsWrapper as BaseAnalyticsWrapper} from '@github-ui/repo-issues-page-layout/AnalyticsWrapper'
import {RepoIssuesPageLayout} from '@github-ui/repo-issues-page-layout/RepoIssuesPageLayout'
import {SharedDashboardBlankslate} from '@github-ui/shared-dashboard-lists/shared/SharedDashboardBlankslate'
import {useCallback, useMemo} from 'react'

import {useIssuesIndexResults} from '../hooks/use-issues-index-results'
import {IssuesListSurface} from '../components/IssuesListSurface'
import {IssuesIndexSearch} from './IssuesIndexSearch'
import {issuesIndexContentRoute, issuesLabelContentRoute} from './issues-index-content-route'
import {issuesLayoutRoute} from './issues-layout-route'
import {useRenderSidebar} from './render-issues-sidebar'

function isSameLocation(a: Location, b: Location): boolean {
  return a.pathname === b.pathname && a.search === b.search
}

/**
 * Repo Issues#index content: a repo analog of `IssuesDashboardSurfaceContent` that
 * reuses the shared `IssuesList`, pagination, skeleton/blankslate, and a repo-scoped
 * `<Filter>` to render the mixed (issues + PRs) result list.
 */
export function IssuesIndexContent() {
  const navigation = useNavigation()
  const location = useLocation()
  const isNavigating = Boolean(navigation.location && !isSameLocation(navigation.location, location))
  const contentRoute = location.pathname.split('/')[3] === 'labels' ? issuesLabelContentRoute : issuesIndexContentRoute
  const {owner, repo} = useRouteParams(contentRoute)
  const {data: layoutPayload} = useRouteQuery(issuesLayoutRoute, 'mainQuery')
  const renderSidebar = useRenderSidebar(owner, repo, layoutPayload)

  return (
    <BaseAnalyticsWrapper category="Repository Issues Index" appName="issues">
      <RepoIssuesPageLayout renderSidebar={renderSidebar} initialExpanded={layoutPayload?.sidebarExpanded}>
        <IssuesIndexSearch />
        <ErrorBoundary
          boundaryName="IssuesIndexContent"
          fallback={
            <div className="tmp-my-3">
              <SharedDashboardBlankslate dashboardType="issues" variant="error" />
            </div>
          }
        >
          <IssuesIndexPageContent isNavigating={isNavigating} />
        </ErrorBoundary>
      </RepoIssuesPageLayout>
    </BaseAnalyticsWrapper>
  )
}

function IssuesIndexPageContent({isNavigating}: {isNavigating: boolean}) {
  const location = useLocation()
  const contentRoute = location.pathname.split('/')[3] === 'labels' ? issuesLabelContentRoute : issuesIndexContentRoute

  const {data: layoutPayload} = useRouteQuery(issuesLayoutRoute, 'mainQuery')
  const {data} = useSuspenseRouteQuery(contentRoute, 'mainQuery')
  const {owner, repo} = useRouteParams(contentRoute)
  const {currentPage, fetchResults, fetchedQuery, getPageHref, getQueryHref} = useIssuesIndexResults()
  const scopedRepository = useMemo(() => ({owner, name: repo}), [owner, repo])

  const applySectionFilter = useCallback(
    (query: string) => {
      fetchResults({q: query})
    },
    [fetchResults],
  )
  const filterActions = useRepositoryFilterActions({
    repo: scopedRepository,
    query: fetchedQuery,
    applySectionFilter,
    issueTypesEnabled: layoutPayload?.issueTypesEnabled ?? false,
  })

  const error = data.error || null

  if (error) {
    return (
      <div className="tmp-my-3">
        <SharedDashboardBlankslate dashboardType="issues" variant="error" />
      </div>
    )
  }

  return (
    <IssuesListSurface
      response={data}
      routeAdapter={{
        scopeIdentity: `repository:${owner}/${repo}`,
        query: fetchedQuery,
        page: currentPage,
        fetch: ({query, page}) => fetchResults({q: query, page}),
        getPageHref,
        getQueryHref,
        invalidate: () => fetchResults({q: fetchedQuery, page: currentPage}),
      }}
      filterActions={filterActions}
      defaultSortingKey={layoutPayload?.bestMatchSortEnabled ? 'relevance' : 'created'}
      isLoading={isNavigating}
    />
  )
}
